import { useAuth } from "@mindlercare/auth-ui";
import { Spinner } from "@mindlercare/auth-ui/src/components/Spinner";
import { defaultTheme } from "@mindlercare/ui-web/src/shared/constants";
import { Tab, Tabs } from "@mui/material";
import { ReactQueryV5 } from "@mindlercare/mindlerapi-client";
const { useQuery } = ReactQueryV5;
import { useMindlerApi } from "api/apiv2";
import { useGetPsychologistIcbtTasks } from "api/useIcbtQueries";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import * as R from "remeda";
import { styled } from "styled-components";
import { Typography } from "components/typography";
import { TodoTasksTable } from "./TodoTasksTable";
import { logger } from "core/logger";
import { usePsychologistUser, useQueryErrorLogger } from "hooks";
import { useGetQuestionnaireCountsPerProgramQuery } from "hooks/icbt/useGetQuestionnaireCountsPerProgramQuery";
import { EmptyContainer } from "components/lib/EmptyContainer";
import { useGetBulkUserAnswersQuery } from "api/useQuestionnaireQueries";
import { parseAsStringLiteral, useQueryState } from "nuqs";
import { useSnackbar } from "@mindlercare/ui-web";
import { DoneTasksTable } from "./DoneTasksTable";

const tabOptions = ["todo", "done"] as const;
type Tab = (typeof tabOptions)[number];

export const IcbtTasks = () => {
  const { t } = useTranslation();

  const { enqueueSnackbar } = useSnackbar();

  const [tab, setTabState] = useQueryState(
    "tab",
    parseAsStringLiteral(tabOptions).withDefault("todo"),
  );

  const [newActivityTab] = useQueryState(
    "newActivity",
    parseAsStringLiteral(tabOptions).withDefault("todo"),
  );

  const handleChange = (_: unknown, newTab: Tab) => setTabState(newTab);

  const {
    data: icbtTasks,
    isLoading: isLoadingIcbtTasks,
    error: icbtTasksError,
  } = useGetPsychologistIcbtTasks();

  const psychologistId = usePsychologistUser().psychologist?.psychologistId;
  const api = useMindlerApi();

  const patientUserIds = useMemo(
    () => [...new Set(icbtTasks?.map((icbt) => Number(icbt.patientUserId)))],
    [icbtTasks],
  );

  const { userId: psychologistUserId } = useAuth();
  if (patientUserIds.length > 20) {
    logger.log(
      "Patient User Ids for patients.getByUserIds (iCBT feedback page)",
      {
        userIdsLength: patientUserIds.length,
        userIds: patientUserIds,
        psychologistUserId,
      },
    );
  }

  const {
    data: patients,
    isLoading: isLoadingPatients,
    error: patientsError,
  } = useQuery(
    api.patients.getByUserIds.queryOptions(
      {
        userIds: patientUserIds,
        psychologistId: psychologistId as number, // safe to cast due to enabled check
      },
      { enabled: patientUserIds.length > 0 && !!psychologistId },
    ),
  );

  const {
    data: questionnaireCounts,
    isLoading: isLoadingQuestionnaireCounts,
    error: questionnaireCountsError,
  } = useGetQuestionnaireCountsPerProgramQuery();

  const {
    data: bulkUserAnswers,
    isLoading: isLoadingBulkUserAnswers,
    error: bulkUserAnswersError,
  } = useGetBulkUserAnswersQuery({
    userIds: patientUserIds.map((id) => id.toString()),
    answerRequestType: "icbt",
  });
  const isLoading =
    isLoadingIcbtTasks ||
    isLoadingPatients ||
    isLoadingQuestionnaireCounts ||
    isLoadingBulkUserAnswers;

  const noData =
    !icbtTasks || !patients || !questionnaireCounts || !bulkUserAnswers;

  const possiblyErrors = {
    icbtTasksError,
    patientsError,
    questionnaireCountsError,
    bulkUserAnswersError,
  };
  const isError = useQueryErrorLogger(
    possiblyErrors,
    "Failed to load data in icbt feedback page",
    {
      team: "treatment",
      tag: "icbtFeedback",
      patientUserIds,
    },
  );

  if (isLoading || !questionnaireCounts) {
    return (
      <PageContainer>
        <Spinner />
      </PageContainer>
    );
  }

  if (noData || isError) {
    enqueueSnackbar({
      text: t("ICBT_LOADING_DATA_ERROR"),
      isError: true,
    });
    return <EmptyContainer />;
  }

  // This is a mapping from userId -> requestId -> surveyId (unique answers)
  // This way we can easily answer to query for "has user has answered to survey(s) attached to this request"
  const uniqueBySurveyIdUserAnswersPerRequestIdPerUserId = R.mapValues(
    bulkUserAnswers,
    (userAnswers) =>
      R.pipe(
        userAnswers,
        R.groupBy(({ requestId }) => requestId),
        R.mapValues(R.uniqueBy(({ surveyId }) => surveyId)),
      ),
  );
  return (
    <PageContainer>
      <PageHeader>
        <Typography insensitive variant="h1">
          {t("ICBT_FEEDBACK_TITLE")}
        </Typography>
        <Typography variant="body" color="DARK_GREY">
          {t("ICBT_TASKS_DESCRIPTION")}
        </Typography>
      </PageHeader>
      <StyledTabs value={tab} onChange={handleChange}>
        <StyledTab
          value="todo"
          label={t("TODO")}
          showDot={newActivityTab === "todo"}
        />
        <StyledTab
          value="done"
          label={t("DONE")}
          showDot={newActivityTab === "done"}
        />
      </StyledTabs>
      {tab === "todo" ? (
        <TodoTasksTable
          icbtTasks={icbtTasks}
          patients={patients}
          questionnaireCounts={questionnaireCounts}
          userAnswersByRequestIdByUserId={
            uniqueBySurveyIdUserAnswersPerRequestIdPerUserId
          }
        />
      ) : null}
      {tab === "done" ? (
        <DoneTasksTable
          icbtTasks={icbtTasks}
          patients={patients}
          questionnaireCounts={questionnaireCounts}
          userAnswersByRequestIdByUserId={
            uniqueBySurveyIdUserAnswersPerRequestIdPerUserId
          }
        />
      ) : null}
    </PageContainer>
  );
};

const PageContainer = styled.div`
  padding: 20px;
`;
const PageHeader = styled.div`
  display: flex;
  padding-bottom: 16px;
  flex-direction: column;
  gap: 4px;
`;

const StyledTabs = styled(Tabs)`
  display: flex;
  margin-bottom: 20px;
`;

const StyledTab = styled(Tab).withConfig({
  shouldForwardProp: (prop) => prop !== "showDot",
})<{ showDot?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  position: relative;
  overflow: visible;

  &:hover {
    color: ${(p) => defaultTheme.COLORS.GRAY_BLACK};
    transition: all 200ms ease-in-out;
  }
  ${({ showDot }) =>
    showDot &&
    `
    &::after {
      content: "";
      position: absolute;
      top: 4px;
      right: -3px;
      width: 8px;
      height: 8px;
      background-color: ${defaultTheme.COLORS.ORANGE};
      border-radius: 50%;
    }
  `}
`;
